import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment-timezone';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import AutoSelect from 'forms/AdminBulkOrderForm/AutoSelect';
import { toastr } from 'react-redux-toastr';
import cache from 'utilities/cache';
import { request, asyncListAll } from 'utilities/graph';
import { getOrdersByClientByDate, getClient } from './queries';

import {
  TIME_ZONE,
} from '@silvergatedelivery/constants';

import issueReceipts from './issueReceipts';

const monthOptions = Array(10)
  .fill('')
  .map((x, index) => {
    return moment().add(-index, 'months').format('YYYY-MM');
  });

export default function GroupPurchaseReceipt({ clientOptions = [] }) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);
  const [selectedClientId, setSelectedClientId] = useState();
  const [includeUndeliveredOrders, setIncludeUndeliveredOrders] = useState(false);
  const cacheKey = 'app:report:groupPurchaseReceipt:uiState';

  const updateCache = (newState = {}) => {
    cache.set(cacheKey, {
      includeUndeliveredOrders,
      ...newState,
    });
  };

  const collectData = async () => {
    const from = moment(selectedMonth).tz(TIME_ZONE).startOf('month');
    const to = moment(selectedMonth).tz(TIME_ZONE).endOf('month');
    const fromDate = from.format('YYYY-MM-DD');
    const toDate = to.format('YYYY-MM-DD');
    const period = `${fromDate}__${toDate}`;
    const { id: clientId } = clientOptions.find(({ id }) => id === selectedClientId);

    global.logger.debug({
      selectedClientId,
      period,
      selectedMonth,
      fromDate,
      toDate,
    });

    const [
      orders,
      { data: { getClient: client } },
    ] = await Promise.all([
      asyncListAll(getOrdersByClientByDate, {
        clientId,
        date: {
          between: [fromDate, toDate],
        },
        filter: {
          groupPurchaseStatus: { eq: 'accepted' },
        },
        limit: 1000,
      }),
      request(getClient, { id: clientId }),
    ]);

    // 收集所有不重複的 individualId
    const individualsSet = new Set(orders.map((order) => order.individualId).filter(Boolean));

    if (individualsSet.size === 0) {
      toastr.error('沒有找到任何個人的團購訂單');
      return;
    }

    // 獲取所有 individual 的資料
    const individualClients = await Promise.all(
      Array.from(individualsSet).map(async (individualId) => {
        try {
          const { data: { getClient: individualClient } } = await request(getClient, { id: individualId });
          return individualClient;
        } catch (error) {
          global.logger.debug(`Failed to get individual client for ${individualId}:`, error);
          return null;
        }
      }),
    );


    // 過濾掉獲取失敗的 individual
    const validIndividualClients = individualClients.filter(Boolean);

    if (validIndividualClients.length === 0) {
      toastr.error('無法獲取個人客戶資料');
      return;
    }

    // 準備所有 individual's 訂單，並帶上 individual 資訊
    const allGroupPurchaseOrders = [];

    for (const individualClient of validIndividualClients) {
      const individualOrders = orders.filter((order) => order.individualId === individualClient.id);
      if (individualOrders.length === 0) {
        console.log(`找不到 ${individualClient.name} 的訂單`);
        continue;
      }

      let filteredOrders = individualOrders;
      if (!includeUndeliveredOrders) {
        filteredOrders = individualOrders.filter(({ status }) =>
          ['delivered', 'completed'].includes(status),
        );
      }
      if (filteredOrders.length === 0) {
        console.log(`找不到 ${individualClient.name} 的訂單`);
        continue;
      }

      filteredOrders.forEach((order) => {
        allGroupPurchaseOrders.push({
          id: order.id,
          ordererName: order.elder?.name || '未知訂購者',
          ordererPhone: order.elder?.phoneNumber || '',
          ordererAddress: order.elder?.address ?
            `${order.elder.address.street || ''} ${order.elder.address.district || ''} ${order.elder.address.county || ''}` : '',
          items: order.mealItems || [],
          total: order.total || 0,
          date: order.date,
          status: order.status,
          individual: {
            id: individualClient.id,
            name: individualClient.name,
            address: {
              street: individualClient.address.street,
              district: individualClient.address.district,
              county: individualClient.address.county,
            },
            phoneNumber: individualClient.phoneNumber,
            taxIdNumber: individualClient.receiptInfo?.taxIdNumber || '',
          },
        });
      });
    }

    const facility = {
      id: clientId,
      name: client.name,
      address: {
        street: client.address.street,
        district: client.address.district,
        county: client.address.county,
      },
      taxIdNumber: client.facilitySettings.receiptInfo?.taxIdNumber || '',
    };

    const stampS3Key = client.facilitySettings.companyStampS3Keys?.[0];

    if (allGroupPurchaseOrders.length === 0) {
      toastr.error('沒有符合設定條件的團購訂單');
      return;
    }

    await issueReceipts({
      groupPurchaseOrders: allGroupPurchaseOrders,
      selectedMonth,
      client: {
        id: clientId,
        name: client.name,
      },
      facility,
      includeUndeliveredOrders,
      stampS3Key,
    });
  };

  const triggerDownload = async () => {
    try {
      setIsLoading(true);
      await collectData();
    } catch (e) {
      global.logger.debug(e);
      toastr.error('下載失敗', '請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (clientOptions[0]) {
      setSelectedClientId(clientOptions[0].id);
    }
    const cached = cache.get(cacheKey);
    if (cached) {
      setIncludeUndeliveredOrders(!!cached.includeUndeliveredOrders);
    }
  }, [clientOptions]);

  return (
    <div>
      <h3>團購請款單</h3>
      <div>
        <FormControl style={{ minWidth: 360, marginRight: 16 }}>
          <AutoSelect
            id='selected-restaurant'
            options={clientOptions.map(({ name }) => name)}
            values={clientOptions.map(({ id }) => id)}
            required={true}
            value={selectedClientId}
            label='機構'
            onChange={(value) => {
              setSelectedClientId(value);
            }}
          />
        </FormControl>
        <FormControl style={{ width: 150, marginRight: 16 }}>
          <InputLabel id='selected-month'>月份</InputLabel>
          <Select
            labelId='selected-month-label'
            id='selected-month'
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            disabled={isLoading}
          >
            {monthOptions.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeUndeliveredOrders}
              name={'includeUndeliveredOrders'}
              value={includeUndeliveredOrders}
              disabled={!selectedClientId || isLoading}
              onChange={(e) => {
                setIncludeUndeliveredOrders(e.target.checked);
                updateCache({ includeUndeliveredOrders: e.target.checked });
              }}
            />
          }
          label={'包含未送達的訂單'}
        />
      </div>

      <Button
        variant='outlined'
        onClick={triggerDownload}
        disabled={!selectedClientId || isLoading}
        style={{ marginTop: 16 }}
      >
        下載
        {isLoading && (
          <CircularProgress
            color='primary'
            size={16}
            style={{ marginLeft: 8 }}
          />
        )}
      </Button>
    </div>
  );
}

GroupPurchaseReceipt.propTypes = {
  clientOptions: PropTypes.array,
};
